import { TableColumnType, Table as CustomTable } from '@/components';
import { Space, Table, Typography } from 'antd';
import { useMemo } from 'react';
import { PersonalizationTableRowValues } from './PersonalizationDetails';
import styles from './PersonalizationSummary.module.scss';
import { PersonalizationSummarySubTotalInfo } from './PersonalizationSummarySubtotalInfo';
import { SalesTaxType, SpecialRequestStatus } from '@/core';
import { PersonalizationTag } from './PersonalizationTag';

interface Props {
  className?: string;
  title?: string;
  subTitle?: string;
  name: string;
  dataSource: PersonalizationTableRowValues[];
  columns: TableColumnType<PersonalizationTableRowValues>[];
  loading?: boolean;
  subtotalTitle?: string;
  showBaseCost?: boolean;
  showResidentPrice?: boolean;
  isPublic?: boolean;
  salesTaxType?: SalesTaxType;
  inactive?: boolean;
}

const Title = Typography.Title;

export const useGetPersonalizationItemsSubtotalAmount = (
  data: PersonalizationTableRowValues[],
  by: (item: PersonalizationTableRowValues) => number | undefined,
  show?: boolean,
  checkSpecialRequests?: boolean,
): { amount?: number; isFinalAmount?: boolean } => {
  return useMemo(() => {
    if (!show) return {};
    const filteredData = checkSpecialRequests
      ? data.filter(
          (x) =>
            !x.specialRequest ||
            (x.specialRequest.status !== SpecialRequestStatus.Denied &&
              x.specialRequest.status !==
                SpecialRequestStatus.ResidentDeclined),
        )
      : data;
    const isFinalAmount = !filteredData.some(
      (x) =>
        x.specialRequest &&
        x.specialRequest.status !== SpecialRequestStatus.ConstructionApproved &&
        x.specialRequest.status !== SpecialRequestStatus.Denied &&
        x.specialRequest.status !== SpecialRequestStatus.ResidentDeclined,
    );

    return {
      amount: filteredData
        ? filteredData.reduce((acc, x) => acc + (by(x) ?? 0), 0)
        : filteredData,
      isFinalAmount,
    };
  }, [data, by, show, checkSpecialRequests]);
};

export function PersonalizationItemList(props: Props) {
  const {
    className,
    dataSource,
    columns,
    title,
    subTitle,
    name,
    subtotalTitle,
    loading,
    showBaseCost,
    showResidentPrice,
    isPublic,
    salesTaxType,
    inactive,
  } = props;

  const { amount: subtotalResidentPriceAmount } =
    useGetPersonalizationItemsSubtotalAmount(
      dataSource,
      (x) =>
        salesTaxType === SalesTaxType.SummaryLevel
          ? x.personalizationItem?.pricing?.residentPriceWithoutTax ??
            x.specialRequest?.prices?.residentPriceWithoutTax
          : x.totalResidentPrice,
      showResidentPrice,
    );

  const { amount: subtotalBaseCostAmount } =
    useGetPersonalizationItemsSubtotalAmount(
      dataSource,
      (x) => x.totalBaseCost,
      showBaseCost,
    );

  const rowClassName = inactive ? styles.inactiveRow : undefined;

  return (
    <Space direction="vertical" size="large" className={className}>
      {title && (
        <Title level={4} className={styles.title}>
          {title}
        </Title>
      )}
      {subTitle && <PersonalizationTag value={subTitle} />}
      {isPublic ? (
        <Table<PersonalizationTableRowValues>
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={loading}
          scroll={{ x: 100 }}
          rowKey={(value) =>
            value.personalizationItem?.id ?? value.specialRequest?.id ?? ''
          }
          rowClassName={rowClassName}
        />
      ) : (
        <CustomTable<PersonalizationTableRowValues>
          name={name}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          loading={loading}
          hideDefaultActions
        />
      )}

      {!isPublic && subtotalTitle && (
        <PersonalizationSummarySubTotalInfo
          title={subtotalTitle}
          baseCostSubtotal={subtotalBaseCostAmount}
          residentPriceSubtotal={subtotalResidentPriceAmount}
        />
      )}
    </Space>
  );
}
